import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPrint } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import PageHeader from "../../ui/PageHeader";
import { useKarkhanaTitle } from "../../ui/addressState";
import CashbookGuard from "../CashbookGuard";
import { refusalText } from "../money";
import type { PrintView } from "../types";
import CashSheetPrint from "./CashSheetPrint";
import { takeFreshPrint } from "./cashPrintFlow";
import "../cashbook.scss";

/**
 * One frozen print (V1_SPEC 11.4 P0). Just made here: prints straight away. Opened again later: the
 * same paper with a diagonal "कॉपी · Copy" mark, so a second copy never passes for the one to pay from.
 */
const PrintScreen = () => {
  const { t } = useKT();
  const { printId } = useParams<{ printId: string }>();
  const [print, setPrint] = useState<PrintView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isFresh = useRef<boolean | null>(null);
  if (isFresh.current === null) isFresh.current = takeFreshPrint(printId);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getPrint(printId);
      if (res?.success) setPrint(res.data);
      else setError(refusalText(res, t));
    } catch {
      setError(t("Not saved. Check the network and retry."));
    }
  }, [printId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!print || !isFresh.current) return;
    const timer = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timer);
  }, [print]);

  useKarkhanaTitle(print ? `${print.sheetNo} · Print ${print.revision}` : null);
  const missing = print ? Array.from({ length: print.pages }, (_, i) => i + 1).filter((p) => !print.pagesSent.includes(p)) : [];

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title={print ? `${t("Pay sheet")} ${print.sheetNo}` : t("Pay sheet")} back={print ? kp.paySheet(print.paySheetId) : kp.paySheets()} backLabel={t("Back")} />
      {error ? (
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      ) : null}
      {!print && !error ? <div className="cb-skeleton is-big" /> : null}
      {print ? (
        <>
          <div className="kw-toolbar">
            <button type="button" className="kh-btn is-primary" onClick={() => window.print()}>
              {t("Print")}
            </button>
            {print.state === "LIVE" && missing.length ? (
              <Link to={kp.cashCamera({ kind: "PAY_SHEET", printId: print.printId, page: missing[0] })} className="kh-btn">
                {t("Send photo")}
              </Link>
            ) : null}
          </div>
          {print.state === "SUPERSEDED" ? (
            <p className="cb-card is-warn">{t("This print was replaced by a newer one. Pay only from the newest print.")}</p>
          ) : null}
          <div className="cbp-screen">
            <CashSheetPrint print={print} copy={!isFresh.current} />
          </div>
        </>
      ) : null}
    </div>
  );
};

const CashSheetPrintPage = () => (
  <CashbookGuard>
    <PrintScreen />
  </CashbookGuard>
);

export default CashSheetPrintPage;
